/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useMemo, useState } from 'react';
import * as THREE from "three";
import { TextureLoader } from 'three';
import { useLoader, useThree } from '@react-three/fiber';

import { const_var, params } from '../../../../../redux/reducers/reducer';
import { MetalStripeImg } from '../../../../../utils/Textures';
import { extrudeSettings, roofCutShape, roofPattern, roofPatternCurve, textureAnisotropy } from '../../../../../utils/Functions';
import { CSG } from '../../../../../assets/libs/csg-lib';
import { controls } from '../../../../../redux/reducers/eventReducer';

const trimThickness = 0.2;
const ridgeThickness = 0.022;
const roofThickness = 0.005;
const roofOutlineThickness = 0.02;

const roofTextureFunc = (texture_metalSprite, gl, p_r_s) => {
    if(texture_metalSprite !== undefined && texture_metalSprite !== "") {
        const verticalMap = texture_metalSprite.clone();
        textureAnisotropy(gl, verticalMap, 1.3, 1.3, Math.PI / 2);
        verticalMap.needsUpdate = true;
        const horizontalMap = texture_metalSprite.clone();
        textureAnisotropy(gl, horizontalMap, 1.3, 1.3);
        horizontalMap.needsUpdate = true;

        if (p_r_s === 1 || p_r_s === 2) {
            return verticalMap;
        } else if (p_r_s === 3) {
            return horizontalMap;
        }
    }
};

const trimOffsetHeightFunc = (p_r_p) => { return p_r_p / 12 * trimThickness }

const rotateAroundCustomAxis = (direction, mesh, angle, trimOffsetHeight) => {

    const rightLeanPoint1 = new THREE.Vector3((params.p_w / 2 + trimThickness), params.p_h - trimOffsetHeight, -params.p_d / 2); 
    const rightLeanPoint2 = new THREE.Vector3((params.p_w / 2 + trimThickness), params.p_h - trimOffsetHeight, params.p_d / 2);
    const rightLeanAxisVector = new THREE.Vector3().subVectors(rightLeanPoint2, rightLeanPoint1).normalize();
    const rightLeanPivotPoint  = new THREE.Vector3((params.p_w / 2 + trimThickness), params.p_h - trimOffsetHeight, 0) 
    
    const leftLeanPoint1 = new THREE.Vector3(-(params.p_w / 2 + trimThickness), params.p_h - trimOffsetHeight, -params.p_d / 2); 
    const leftLeanPoint2 = new THREE.Vector3(-(params.p_w / 2 + trimThickness), params.p_h - trimOffsetHeight, params.p_d / 2);
    const leftLeanAxisVector = new THREE.Vector3().subVectors(leftLeanPoint2, leftLeanPoint1).normalize();
    const leftLeanPivotPoint  = new THREE.Vector3(-(params.p_w / 2 + trimThickness), params.p_h - trimOffsetHeight, 0) 

    let pivotPoint, axis;
    if (direction === 'left') {
        pivotPoint = leftLeanPivotPoint;
        axis = leftLeanAxisVector
    } else {
        pivotPoint = rightLeanPivotPoint;
        axis = rightLeanAxisVector
    }
    
    // Create translation matrices
    const translationMatrix1 = new THREE.Matrix4().makeTranslation(-pivotPoint.x, -pivotPoint.y, -pivotPoint.z);
    const translationMatrix2 = new THREE.Matrix4().makeTranslation(pivotPoint.x, pivotPoint.y, pivotPoint.z);

    // Create rotation matrix
    const rotationMatrix = new THREE.Matrix4().makeRotationAxis(axis, angle);

    // Apply the transformation: translate to pivot, rotate, then translate back
    mesh.geometry.applyMatrix4(translationMatrix1);
    mesh.geometry.applyMatrix4(rotationMatrix);
    mesh.geometry.applyMatrix4(translationMatrix2);

    // Update the mesh's world matrix
    mesh.updateMatrixWorld();
};

const RoofModel = () => {
    const { gl } = useThree();
    // const baseTexture = useLoader(THREE.TextureLoader, TrimTexture);
    const canvasElement = document.getElementsByClassName("building-size")
    const isPattern = (canvasElement != undefined && canvasElement[0] && canvasElement[0].clientWidth > 1050 ) ? true : false
    
    const [overHangLeftWidth, setOverHangLeftWidth] = useState(0);
    const [overHangRightWidth, setOverHangRightWidth] = useState(0);
    const [roofLength, setRoofLength] = useState(params.p_d);
    const [regularHeight, setRegularHeight] = useState(0);
    const [isEqualLeftHeight, setIsEqualLeftHeight] = useState(false);
    const [isEqualRightHeight, setIsEqualRightHeight] = useState(false);
    const [angle, setAngle] = useState({x:0,y:0,z:0});
    const [patternLength, setPatternLength] = useState(0);
    const [patternDepth, setPatternDepth] = useState(roofLength);
    const [patternPosition, setPatternPosition] = useState({x:0,y:params.p_h + (( params.p_r_p / 12)*(params.p_w/2))+0.02,z:roofLength/-2});
   
    const mainColor = useMemo(() => params.p_r_c.replace('0x', '#'), [params.p_r_c]);
    
    const texture_metalSprite = const_var.metalTexture; 
   
    // let roofTexture = roofTextureFunc(texture_metalSprite, gl, params.p_r_s)
    let trimOffsetHeight = trimOffsetHeightFunc(params.p_r_p)
    
    const overHangLeftHeight = useMemo(() => {
        return params.p_r_s != 1 ? (((isEqualLeftHeight === true ? params.b_l_t_r_p : params.p_r_p) / 12) * (overHangLeftWidth - trimThickness) + trimOffsetHeight): ((params.p_r_p / 12) * overHangLeftWidth);
    }, [params.p_r_s, params.b_l_t_r_p, params.p_r_p, isEqualLeftHeight, overHangLeftWidth])

    const overHangRightHeight = useMemo(() => {
        return params.p_r_s != 1 ? (((isEqualRightHeight === true ? params.b_l_t_r_pR : params.p_r_p) / 12) * (overHangRightWidth - trimThickness) + trimOffsetHeight): ((params.p_r_p / 12) * overHangRightWidth);
    }, [params.p_r_s, params.b_l_t_r_pR, params.p_r_p, isEqualRightHeight, overHangRightWidth])

    const dynamicHeight = useMemo(() => {
        return params.singleSlope ? ((params.p_r_p / 12) * (params.p_w)) : 0;
    }, [params.singleSlope, params.p_r_p, params.p_w])

    const singleSlopeOverHang = useMemo(() => (params.singleSlope ? -overHangRightHeight : overHangRightHeight), [params.singleSlope, overHangRightHeight])

    const centerRoofAngle = useMemo(() => (Math.atan2((params.p_r_p / 12), 1)), [params.p_r_p])

    const leftLeanRoofAngle = useMemo(() => {
        const leftLeanRoofPitchHeight = (params.b_l_t_r_p / 12) * params.lean_p_w
        return Math.atan((leftLeanRoofPitchHeight - (isEqualLeftHeight && trimOffsetHeight)) / (params.lean_p_w - (isEqualLeftHeight && trimThickness)));
    },[params.b_l_t_r_p, params.lean_p_w, isEqualLeftHeight, params.lean_p_w, params.add_left_front_lean_porch, params.add_left_back_lean_porch,]) 

    const rightLeanRoofAngle = useMemo(() => {
        const rightLeanRoofPitchHeight = (params.b_l_t_r_pR / 12) * params.leanR_p_w;
        const angle = Math.atan((rightLeanRoofPitchHeight - (isEqualRightHeight && trimOffsetHeight)) / (params.leanR_p_w - (isEqualRightHeight && trimThickness)))
        return angle
    }, [isEqualRightHeight, params.leanR_p_w, params.b_l_t_r_pR, params.leanR_p_w])

    const roofModelOutlineLeft = useMemo(() => {
        const model = new THREE.Shape();
        model.moveTo(-params.p_w / 2 - overHangLeftWidth, 0 - overHangLeftHeight);
        model.lineTo(-params.p_w / 2 - overHangRightWidth - 0.001, 0 - overHangRightHeight);
        model.lineTo(-params.p_w / 2 - overHangRightWidth - 0.001, roofOutlineThickness - overHangRightHeight);
        model.lineTo(-params.p_w / 2 - overHangLeftWidth, roofOutlineThickness - overHangLeftHeight);
        model.closePath();

        return model;

    }, [params.p_w, params.p_r_s, params.p_r_p, regularHeight, overHangLeftWidth, overHangLeftHeight, overHangRightWidth, overHangRightHeight]);

    const roofModelOutlineRight = useMemo(() => {
        const model = new THREE.Shape();
        model.moveTo(params.p_w / 2 + overHangLeftWidth, 0 - singleSlopeOverHang);
        model.lineTo(params.p_w / 2 + overHangRightWidth + 0.001, 0 - singleSlopeOverHang);
        model.lineTo(params.p_w / 2 + overHangRightWidth + 0.001, roofOutlineThickness - singleSlopeOverHang);
        model.lineTo(params.p_w / 2 + overHangLeftWidth, roofOutlineThickness - singleSlopeOverHang);
        model.closePath();

        return model;

    }, [params.p_w, params.p_r_s, params.p_r_p, regularHeight, overHangLeftWidth, overHangLeftHeight, overHangRightWidth, overHangRightHeight]);

    const roofModelOutlineFrontBack = useMemo(() => {
        const model = new THREE.Shape();

        if(params.singleSlope && params.freeStandingSide === 'left'){
            model.moveTo(-params.p_w / 2 - overHangLeftWidth, 0 - overHangLeftHeight - dynamicHeight);
            model.lineTo(params.p_w / 2 + overHangRightWidth + 0.001, 0 - singleSlopeOverHang);
            model.lineTo(params.p_w / 2 + overHangRightWidth + 0.001, roofOutlineThickness - singleSlopeOverHang);
            model.lineTo(-params.p_w / 2 - overHangLeftWidth, roofOutlineThickness - overHangLeftHeight - dynamicHeight);
            model.closePath();
        } else {
            model.moveTo(-params.p_w / 2 - overHangLeftWidth, roofOutlineThickness - overHangLeftHeight - dynamicHeight);
            model.lineTo(0, params.p_w / 2 * params.p_r_p / 12 + 0.018);
            model.lineTo(params.p_w / 2 + overHangRightWidth + 0.001, roofOutlineThickness - singleSlopeOverHang);
            model.lineTo(params.p_w / 2 + overHangRightWidth + 0.001, 0 - singleSlopeOverHang);
            model.lineTo(0, params.p_w / 2 * params.p_r_p / 12);
            model.lineTo(-params.p_w / 2 - overHangLeftWidth, 0 - overHangLeftHeight - dynamicHeight);
            model.closePath();
        }

        return model;

    }, [params.p_w, params.p_r_s, params.p_r_p, regularHeight, overHangLeftWidth, overHangLeftHeight, overHangRightWidth, overHangRightHeight, dynamicHeight]);

    const roofModel = useMemo(() => {      
        const leftRoofEndLength = (overHangLeftWidth - trimThickness) / Math.cos(isEqualLeftHeight ? leftLeanRoofAngle : centerRoofAngle);
        const leftRoofEndOffset = leftRoofEndLength - (overHangLeftWidth - trimThickness) / Math.cos( centerRoofAngle) ;
        const leftRoofEndPointOffset =  {
            x: Math.cos(leftLeanRoofAngle) * leftRoofEndOffset,
            y: Math.sin(leftLeanRoofAngle) * leftRoofEndOffset
        }
        
        const rightRoofEndLength = (overHangRightWidth - trimThickness) / Math.cos(isEqualRightHeight ? rightLeanRoofAngle : centerRoofAngle);
        const rightRoofEndOffset = rightRoofEndLength - (overHangRightWidth - trimThickness) / Math.cos( centerRoofAngle) ;
        const rightRoofEndPointOffset =  {
            x: Math.cos(rightLeanRoofAngle) * rightRoofEndOffset,
            y:Math.sin(rightLeanRoofAngle) * rightRoofEndOffset,
        }

        const model = new THREE.Shape();
        model.moveTo(-params.p_w / 2 - overHangLeftWidth + leftRoofEndPointOffset.x, 0 - overHangLeftHeight - dynamicHeight + leftRoofEndPointOffset.y);
        if (params.p_r_s !== 1) model.lineTo(-params.p_w / 2 - trimThickness, -trimOffsetHeight - dynamicHeight);
        if (params.p_r_s === 1) {
            model.lineTo(-params.p_w / 2 - overHangLeftWidth, 0 - overHangLeftHeight + 0.3);
            model.lineTo(-params.p_w / 2 - overHangLeftWidth + 0.02, 0 - overHangLeftHeight + 0.34);
            model.lineTo(-params.p_w / 2 - overHangLeftWidth + 0.055, 0 - overHangLeftHeight + 0.38);
            model.lineTo(-params.p_w / 2 - overHangLeftWidth + 0.1, 0 - overHangLeftHeight + 0.4);
        }

        if (!params.singleSlope)model.lineTo(0, (params.p_r_p / 12) * params.p_w / 2 + regularHeight);
        if (params.p_r_s !== 1 )model.lineTo(params.p_w / 2 + trimThickness, (params.singleSlope ? trimOffsetHeight : -trimOffsetHeight))

        if (params.p_r_s === 1) {
            model.lineTo(params.p_w / 2 + overHangRightWidth - 0.1, 0 - overHangRightHeight + 0.4);
            model.lineTo(params.p_w / 2 + overHangRightWidth - 0.055, 0 - overHangRightHeight + 0.38);
            model.lineTo(params.p_w / 2 + overHangRightWidth - 0.02, 0 - overHangRightHeight + 0.34);
            model.lineTo(params.p_w / 2 + overHangRightWidth, 0 - overHangRightHeight + 0.3);
        }

        model.lineTo(params.p_w / 2 + overHangRightWidth-rightRoofEndPointOffset.x, 0 - singleSlopeOverHang + rightRoofEndPointOffset.y);
        model.lineTo(params.p_w / 2 + overHangRightWidth-rightRoofEndPointOffset.x , roofThickness - singleSlopeOverHang + rightRoofEndPointOffset.y);
        
        if (params.p_r_s !== 1) model.lineTo(params.p_w / 2 + trimThickness, (params.singleSlope ? trimOffsetHeight : -trimOffsetHeight) + roofThickness)
        if (params.p_r_s === 1) {
            model.lineTo(params.p_w / 2 + overHangRightWidth, 0.001 - overHangRightHeight + 0.3);
            model.lineTo(params.p_w / 2 + overHangRightWidth - 0.02, 0.001 - overHangRightHeight + 0.34);
            model.lineTo(params.p_w / 2 + overHangRightWidth - 0.055, 0.001 - overHangRightHeight + 0.38);
            model.lineTo(params.p_w / 2 + overHangRightWidth - 0.1, 0.001 - overHangRightHeight + 0.4);
        }
        if (!params.singleSlope) model.lineTo(0, (params.p_r_p / 12) * params.p_w / 2 + regularHeight + roofThickness);
        if (params.p_r_s === 1) {
            model.lineTo(-params.p_w / 2 - overHangLeftWidth + 0.1, 0.001 - overHangLeftHeight + 0.4);
            model.lineTo(-params.p_w / 2 - overHangLeftWidth + 0.055, 0.001 - overHangLeftHeight + 0.38);
            model.lineTo(-params.p_w / 2 - overHangLeftWidth + 0.02, 0.001 - overHangLeftHeight + 0.34);
            model.lineTo(-params.p_w / 2 - overHangLeftWidth, 0.01 - overHangLeftHeight + 0.3);
        }
        if (params.p_r_s !== 1) {
            model.lineTo(-params.p_w / 2 - trimThickness, -trimOffsetHeight - dynamicHeight + roofThickness)
            model.lineTo(-params.p_w / 2 - overHangLeftWidth + leftRoofEndPointOffset.x, roofThickness - overHangLeftHeight - dynamicHeight + leftRoofEndPointOffset.y);
            model.closePath();
        }

        return model;

    }, [params.p_w, params.p_r_s, params.lean_p_h, params.leanR_p_h, params.p_r_p, params.o_v_h_n_g_e, params.o_v_h_n_g, regularHeight, overHangLeftWidth, overHangLeftHeight, overHangRightWidth, overHangRightHeight, isEqualLeftHeight, leftLeanRoofAngle, isEqualRightHeight, rightLeanRoofAngle, centerRoofAngle,params.singleSlope]);

    const roofInnerModel = useMemo(() => {
        const model = new THREE.Shape();
        const innerRoofWidth = params.p_w - 0.03;
        const innerRoofPorchSize = params.p_r_p - 0.03;

        model.moveTo(-innerRoofWidth / 2 - overHangLeftWidth, 0 - overHangLeftHeight);
        if (params.p_r_s === 1) {
            model.lineTo(-innerRoofWidth / 2 - overHangLeftWidth, 0 - overHangLeftHeight + 0.3);
            model.lineTo(-innerRoofWidth / 2 - overHangLeftWidth + 0.02, 0 - overHangLeftHeight + 0.34);
            model.lineTo(-innerRoofWidth / 2 - overHangLeftWidth + 0.055, 0 - overHangLeftHeight + 0.38);
            model.lineTo(-innerRoofWidth / 2 - overHangLeftWidth + 0.1, 0 - overHangLeftHeight + 0.39);
        }

        model.lineTo(0, (innerRoofPorchSize / 12) * innerRoofWidth / 2 + regularHeight - 0.007);

        if (params.p_r_s === 1) {
            model.lineTo(innerRoofWidth / 2 + overHangRightWidth - 0.1, 0 - overHangRightHeight + 0.39);
            model.lineTo(innerRoofWidth / 2 + overHangRightWidth - 0.055, 0 - overHangRightHeight + 0.38);
            model.lineTo(innerRoofWidth / 2 + overHangRightWidth - 0.02, 0 - overHangRightHeight + 0.34);
            model.lineTo(innerRoofWidth / 2 + overHangRightWidth, 0 - overHangRightHeight + 0.3);
        }

        model.lineTo(innerRoofWidth / 2 + overHangRightWidth, 0 - overHangRightHeight);
        model.lineTo(innerRoofWidth / 2 + overHangRightWidth, roofThickness - overHangRightHeight);

        if (params.p_r_s === 1) {
            model.lineTo(innerRoofWidth / 2 + overHangRightWidth, 0.001 - overHangRightHeight + 0.3);
            model.lineTo(innerRoofWidth / 2 + overHangRightWidth - 0.02, 0.001 - overHangRightHeight + 0.34);
            model.lineTo(innerRoofWidth / 2 + overHangRightWidth - 0.055, 0.001 - overHangRightHeight + 0.38);
            model.lineTo(innerRoofWidth / 2 + overHangRightWidth - 0.1, 0.001 - overHangRightHeight + 0.39);
        }

        model.lineTo(0, (innerRoofPorchSize / 12) * innerRoofWidth / 2 + regularHeight - 0.007 + roofThickness);

        if (params.p_r_s === 1) {
            model.lineTo(-innerRoofWidth / 2 - overHangLeftWidth + 0.1, 0.001 - overHangRightHeight + 0.39);
            model.lineTo(-innerRoofWidth / 2 - overHangLeftWidth + 0.055, 0.001 - overHangRightHeight + 0.38);
            model.lineTo(-innerRoofWidth / 2 - overHangLeftWidth + 0.02, 0.001 - overHangRightHeight + 0.34);
            model.lineTo(-innerRoofWidth / 2 - overHangLeftWidth, 0.01 - overHangRightHeight + 0.3);
        }

        model.lineTo(-innerRoofWidth / 2 - overHangLeftWidth, roofThickness - overHangRightHeight);
        model.closePath();

        return model;

    }, [params.p_w, params.p_r_p, overHangLeftWidth, overHangLeftHeight, params.p_r_s, regularHeight, overHangRightWidth, overHangRightHeight]);

    const topRidgeModel = useMemo(() => {
        const ridgeWidth = 0.25;
        
        const model = new THREE.Shape();
        model.moveTo(-ridgeWidth, (params.p_r_p / 12) * (params.p_w / 2 - ridgeWidth));

        model.lineTo(0, (params.p_r_p / 12) * params.p_w / 2 + regularHeight);

        model.lineTo(ridgeWidth, (params.p_r_p / 12) * (params.p_w / 2 - ridgeWidth));
        model.lineTo(ridgeWidth, (params.p_r_p / 12) * (params.p_w / 2 - ridgeWidth) + ridgeThickness);

        model.lineTo(0, (params.p_r_p / 12) * params.p_w / 2 + regularHeight + ridgeThickness);

        model.lineTo(-ridgeWidth, (params.p_r_p / 12) * (params.p_w / 2 - ridgeWidth) + ridgeThickness);
        model.closePath();

        return model;
    }, [params.p_w, params.p_r_p, regularHeight]);

    const rightLeanRotationAngle = useMemo(() => {
        return -(rightLeanRoofAngle - centerRoofAngle)
    }, [rightLeanRoofAngle, centerRoofAngle])

    const leftLeanRotationAngle = useMemo(() => {
        return (leftLeanRoofAngle - centerRoofAngle)
    }, [leftLeanRoofAngle, centerRoofAngle])

    // create roof geometry 
    const roofComplexModel = useMemo(() => {
        // *start* left leanTo ----------------------------------------------------------------////
        const leftRoofMeshGeo = new THREE.Mesh(new THREE.ExtrudeGeometry(roofPattern(patternLength,isPattern), extrudeSettings(patternDepth)));
        const leftRoofCutMeshGeo = new THREE.Mesh(new THREE.ExtrudeGeometry(roofCutShape(),  extrudeSettings(roofLength) ))

        let leftRoofmesh = leftRoofMeshGeo.clone();
        let leftTempGroup1 = new THREE.Object3D();
        leftTempGroup1 = new THREE.Object3D();

        if(params.p_r_s == 3){
            if(params.singleSlope){
                leftRoofmesh.position.set(patternPosition.x, patternPosition.y, patternPosition.z);
                leftRoofmesh.rotation.set(angle.x,0, angle.z)
                leftRoofmesh.updateMatrixWorld();
        
                leftTempGroup1.rotation.set(0,-angle.y,0);
            }else{
                leftRoofmesh.position.set(-patternDepth,0,-patternPosition.x);
                leftRoofmesh.rotation.set(0,Math.PI/2,0)
    
                leftTempGroup1.rotation.set(0,0,angle.x);
                leftTempGroup1.position.set(0,patternPosition.y,0);
            }
        }else{
            leftRoofmesh.position.set(patternPosition.x, patternPosition.y, -patternPosition.z);
            leftRoofmesh.rotation.set(angle.x, Math.PI, angle.z)
    
            leftTempGroup1.rotation.set(0,angle.y,0);
        }
        leftTempGroup1.updateMatrixWorld();
        leftRoofmesh.updateMatrixWorld();


        // Apply the parent's world matrix to the roofmesh
        leftRoofmesh.applyMatrix4(leftTempGroup1.matrixWorld);
        leftRoofmesh.updateMatrixWorld();

        let leftRoofCutMesh = leftRoofCutMeshGeo.clone();
        leftRoofCutMesh.position.set(-(params.p_w / 2 + trimThickness), params.p_h - trimOffsetHeight, - (roofLength / 2))
        leftRoofCutMesh.rotation.set(0, 0, leftLeanRoofAngle)
        leftRoofCutMesh.visible = false
        leftRoofCutMesh.updateMatrixWorld();

        let leftSyncRoofCutMesh = leftRoofCutMeshGeo.clone();
        leftSyncRoofCutMesh.position.set(-(params.p_w / 2 + trimThickness), params.p_h - trimOffsetHeight, - (roofLength / 2))
        leftSyncRoofCutMesh.rotation.set(0, 0, (centerRoofAngle))
        leftSyncRoofCutMesh.visible = false
        leftSyncRoofCutMesh.updateMatrixWorld();

        const leftRoofMeshGeoMesh_CSG = CSG.fromMesh(leftRoofmesh);
        const leftRoofCutMeshGeoMesh_CSG = CSG.fromMesh(leftRoofCutMesh);
        const leftSyncRoofCutMeshGeoMesh_CSG = CSG.fromMesh(leftSyncRoofCutMesh);

        // cut roof geometry --------------------------------------------------------------------
        let leftRoofMeshFinalCSG;

        if (params.add_left_lean && isEqualLeftHeight) {
            if (params.p_r_s != 3) {
                leftRoofMeshFinalCSG = leftRoofMeshGeoMesh_CSG.subtract(leftRoofCutMeshGeoMesh_CSG)
            } else {
                leftRoofMeshFinalCSG = leftRoofMeshGeoMesh_CSG.subtract(leftRoofCutMeshGeoMesh_CSG)
            }
        } else {
            leftRoofMeshFinalCSG = leftRoofMeshGeoMesh_CSG
        }

        const leftEndRoofMeshFinalCSG = leftRoofMeshGeoMesh_CSG.intersect(leftSyncRoofCutMeshGeoMesh_CSG)
        const leftEndRoofMeshFinal = CSG.toMesh(leftEndRoofMeshFinalCSG, new THREE.Matrix4())
        rotateAroundCustomAxis('left', leftEndRoofMeshFinal, leftLeanRotationAngle, trimOffsetHeight);

        // *end* left leanTo -------------------------------------------------------------------////

        // *start* right leanTo ----------------------------------------------------------------////
        const rightRoofMeshGeo = new THREE.Mesh(new THREE.ExtrudeGeometry(roofPattern(patternLength,isPattern), extrudeSettings(patternDepth)));
        const rightRoofCutMeshGeo = new THREE.Mesh(new THREE.ExtrudeGeometry(roofCutShape(),  extrudeSettings(roofLength) ))

        let rightRoofmesh = rightRoofMeshGeo.clone();
        rightRoofmesh.position.set(patternPosition.x, patternPosition.y, patternPosition.z);
        rightRoofmesh.rotation.set(angle.x,0,angle.z)
        rightRoofmesh.updateMatrixWorld();

        let leftleftTempGroup1 = new THREE.Object3D();
        leftleftTempGroup1.rotation.set(0,angle.y,0);
        leftleftTempGroup1.updateMatrixWorld();
        
        // Apply the parent's world matrix to the roofmesh
        rightRoofmesh.applyMatrix4(leftleftTempGroup1.matrixWorld);
        rightRoofmesh.updateMatrixWorld();

        let rightRoofCutMesh = rightRoofCutMeshGeo.clone();
        rightRoofCutMesh.position.set((params.p_w / 2 + trimThickness), params.p_h - trimOffsetHeight, - (roofLength / 2))
        rightRoofCutMesh.rotation.set(0, 0, (Math.PI - rightLeanRoofAngle))
        rightRoofCutMesh.visible = false
        rightRoofCutMesh.updateMatrixWorld();
        
        let rightSyncRoofCutMesh = rightRoofCutMeshGeo.clone();
        rightSyncRoofCutMesh.position.set((params.p_w / 2 + trimThickness), params.p_h - trimOffsetHeight, - (roofLength / 2))
        rightSyncRoofCutMesh.rotation.set(0, 0, (Math.PI - centerRoofAngle))
        rightSyncRoofCutMesh.visible = false
        rightSyncRoofCutMesh.updateMatrixWorld();

        const rightRoofMeshGeoMesh_CSG = CSG.fromMesh(rightRoofmesh);
        const rightRoofCutMeshGeoMesh_CSG = CSG.fromMesh(rightRoofCutMesh);
        const rightSyncRoofCutMeshGeoMesh_CSG = CSG.fromMesh(rightSyncRoofCutMesh);

        // cut roof geometry --------------------------------------------------------------------

        let rightRoofMeshFinalCSG;
        if (params.add_right_lean && isEqualRightHeight) {
            if (params.p_r_s != 3) {
                rightRoofMeshFinalCSG = rightRoofMeshGeoMesh_CSG.subtract(rightRoofCutMeshGeoMesh_CSG)
            } else {
                rightRoofMeshFinalCSG = rightRoofMeshGeoMesh_CSG.subtract(rightRoofCutMeshGeoMesh_CSG)
            }
        } else {
            rightRoofMeshFinalCSG = rightRoofMeshGeoMesh_CSG
        }
            
        // *end* right leanTo -------------------------------------------------------------------////
        
        const rightEndRoofMeshFinalCSG = rightRoofMeshGeoMesh_CSG.intersect(rightSyncRoofCutMeshGeoMesh_CSG)
        const rightEndRoofMeshFinal = CSG.toMesh(rightEndRoofMeshFinalCSG, new THREE.Matrix4())
        rotateAroundCustomAxis('right', rightEndRoofMeshFinal, rightLeanRotationAngle, trimOffsetHeight);

        const leftRoofMeshFinal = CSG.toMesh(leftRoofMeshFinalCSG, new THREE.Matrix4())
        const rightRoofMeshFinal = CSG.toMesh(rightRoofMeshFinalCSG, new THREE.Matrix4())

        return {
            leftRoofMeshFinal: leftRoofMeshFinal.geometry,
            leftEndRoofMeshFinal: leftEndRoofMeshFinal.geometry,
            rightRoofMeshFinal: rightRoofMeshFinal.geometry,
            rightEndRoofMeshFinal: rightEndRoofMeshFinal.geometry,
        }

    }, [params.p_w, params.p_h, params.add_left_lean, params.add_right_lean, params.p_r_s, angle, centerRoofAngle, patternPosition, patternLength, patternDepth, roofLength, isEqualLeftHeight, isEqualRightHeight,params.singleSlope])

    useMemo(() => {
        roofTextureFunc(texture_metalSprite, gl, params.p_r_s)
    }, [params.p_r_s])

    useEffect(() => {
        trimOffsetHeight = trimOffsetHeightFunc(params.p_r_p)
    }, [params.p_r_p]);

    let sideOverHangSize = 0 
    let endOverHangSize = 0 
    if(const_var.isOverhang['side']!=undefined && const_var.isOverhang['side'].size){
        sideOverHangSize = Number(const_var.isOverhang['side'].size)
        if(sideOverHangSize<=1){
            sideOverHangSize = (0.25 + sideOverHangSize)
        }
    }
    if(const_var.isOverhang['end']!=undefined && const_var.isOverhang['end'].size){
        endOverHangSize = Number(const_var.isOverhang['end'].size)
    }

    useMemo(() => {
        const leanRoofLeftHeight =  (params.b_l_t_r_p / 12) * params.lean_p_w;
        const leanRoofRightHeight = (params.b_l_t_r_pR / 12) * params.leanR_p_w;

        if(params.add_left_lean && (params.p_h === leanRoofLeftHeight + params.lean_p_h)) {
            setIsEqualLeftHeight(true)
        } else {
            setIsEqualLeftHeight(false)
        }
        if(params.add_right_lean && (params.p_h === leanRoofRightHeight + params.leanR_p_h)) {
            setIsEqualRightHeight(true)
        } else {
            setIsEqualRightHeight(false)
        }
    }, [params.p_h, params.lean_p_h, params.leanR_p_h, params.b_l_t_r_p, params.b_l_t_r_pR, params.add_left_lean, params.add_right_lean, params.lean_p_w, params.leanR_p_w])

    useMemo(() => {
        const diagonalLength =  Math.sqrt(Math.pow((params.p_w / 2) + overHangLeftWidth, 2) + Math.pow((params.p_r_p / 12*((params.p_w / 2) + overHangLeftWidth)), 2));

        if (params.p_r_s === 1) {
            setOverHangLeftWidth(0);
            setOverHangRightWidth(0);
            setRoofLength(params.p_d + 1.05);
            setRegularHeight(0.4);

            setPatternLength(diagonalLength - overHangLeftWidth - 0.1)
            setPatternDepth(roofLength)
            setAngle({x:0,y:0,z:-Math.atan2((params.p_r_p / 12), 1)})
            setPatternPosition({x:0,y:params.p_h + ((params.p_r_p / 12)*(params.p_w/2))+0.02,z:roofLength/-2})
        } else if (params.p_r_s === 2) {
            if(params.o_v_h_n_g === false){
                setOverHangRightWidth(0.75)
                setOverHangLeftWidth(0.75);
            } else {
                setOverHangLeftWidth((sideOverHangSize));
                setOverHangRightWidth((sideOverHangSize));
            }
            if(params.o_v_h_n_g_e === false) {
                setRoofLength(params.p_d + 1.5);
            } else {
                setRoofLength(params.p_d + 1 + endOverHangSize);
            }
            setRegularHeight(0);

            setPatternDepth(roofLength)
            setAngle({x: 0, y: 0, z: -Math.atan2((params.p_r_p / 12), 1)})
            
            if(params.singleSlope) {
                setPatternLength(diagonalLength * 2)
                setPatternPosition({x:params.p_w / 2 + overHangLeftWidth,y:params.p_h + overHangLeftHeight+ 0.02,z:roofLength/-2})
            } else {
                setPatternLength(diagonalLength)
                setPatternPosition({x:0,y:params.p_h + ((params.p_r_p / 12)*(params.p_w/2))+0.02,z:roofLength/-2})
            }
        } else if (params.p_r_s === 3) {
            if(params.o_v_h_n_g === false){
                setOverHangRightWidth(0.75)
                setOverHangLeftWidth(0.75);
            } else {
                setOverHangLeftWidth((sideOverHangSize));
                setOverHangRightWidth((sideOverHangSize));
            }
            if(params.o_v_h_n_g_e === false) {
                setRoofLength(params.p_d + 1.5);
            } else {
                setRoofLength(params.p_d + endOverHangSize * 2);
            }
            setRegularHeight(0);

            setPatternLength(roofLength)
            
            setAngle({x: Math.atan2((params.p_r_p / 12), 1), y: Math.PI / 2,z: 0})
            if (params.singleSlope)  {
                setPatternDepth(diagonalLength * 2)
                setPatternPosition({x: roofLength/-2, y: params.p_h + overHangLeftHeight + 0.02, z: -(params.p_w / 2 + overHangLeftWidth)})
            } else {
                setPatternDepth(diagonalLength)
                setPatternPosition({x: roofLength / -2, y: params.p_h + ((params.p_r_p / 12) * (params.p_w / 2)) + 0.02, z: 0})
            }
        }
    }, [sideOverHangSize,endOverHangSize,params.p_r_s, params.p_w, params.p_d, params.p_h, params.p_r_p, params.singleSlope, params.b_l_t_r_p, params.b_l_t_r_pR, params.o_v_h_n_g_e, params.o_v_h_n_g, roofLength, overHangLeftWidth, overHangLeftHeight]);
    
    const texture = useMemo(() => {
        let texture = const_var.wallTextureObj[params.wallTexture];
        textureAnisotropy(gl, texture, 1.3, 1.3, Math.PI/-2);    
        return texture
    }, [params.p_v_w,params.wallTexture])

    return (
        <group onPointerOver={e => e.stopPropagation()} onPointerDown={e => e.stopPropagation()} onClick={e => e.stopPropagation()} >
            {params.p_r_s === 1 &&
                <>
                    {/* <mesh name="roof-out" position={[0, params.p_h, -roofLength / 2]}>
                        <extrudeGeometry args={[roofModel, extrudeSettings(roofLength)]} />
                        <meshLambertMaterial map={roofTexture} bumpMap={roofTexture} bumpScale={0.02} color={mainColor} side={THREE.DoubleSide} toneMapped={false} />
                    </mesh> */}

                    <group rotation={[0, angle.y, 0]}>
                        <mesh name="roof-out-right"  position={[patternPosition.x, patternPosition.y + 0.37, patternPosition.z]} rotation={[angle.x, 0, angle.z]}>
                            <extrudeGeometry args={[roofPattern(patternLength,true), extrudeSettings(patternDepth)]}/>
                            <meshStandardMaterial  color={mainColor} side={THREE.DoubleSide} roughness={1} metalness={0.35} toneMapped={false} emissive={'#000000'} transparent={params.is_translusant || (controls.isCheckActiveTab == "doorwindow" && const_var.focusedSection !== 'center')} opacity={0.02} />
                        </mesh>
                    </group>

                    <group rotation={[  0,Math.PI,0]}>
                        <mesh name="roof-out-left"  position={[patternPosition.x, patternPosition.y+0.37,-patternPosition.z]} rotation={[-angle.x, 0, angle.z]}>
                            <extrudeGeometry args={[roofPattern(patternLength,true), extrudeSettings(-patternDepth)]}/>
                            <meshStandardMaterial  color={mainColor} side={THREE.DoubleSide} roughness={1} metalness={0.35} toneMapped={false} emissive={'#000000'} transparent={params.is_translusant || (controls.isCheckActiveTab == "doorwindow" && const_var.focusedSection !== 'center')} opacity={0.02} />
                        </mesh>
                    </group>

                    <mesh name="roof-out-right-curve"  position={[params.p_w/2 - 0.1, params.p_h+(0.1*(params.p_r_p/12))+0.39, patternDepth/-2]}>
                        <extrudeGeometry args={[roofPatternCurve(1, params.p_r_p), extrudeSettings(patternDepth)]}/>
                        <meshStandardMaterial  color={mainColor} side={THREE.DoubleSide} roughness={1} metalness={0.35} toneMapped={false} emissive={'#000000'} transparent={params.is_translusant || (controls.isCheckActiveTab == "doorwindow" && const_var.focusedSection !== 'center')} opacity={0.02} />
                    </mesh>

                    <mesh name="roof-out-left-curve"  position={[params.p_w/-2 + 0.1, params.p_h+(0.1*(params.p_r_p/12))+0.39, patternDepth/2]} rotation={[0,Math.PI,0]}>
                        <extrudeGeometry args={[roofPatternCurve(1, params.p_r_p), extrudeSettings(patternDepth)]}/>
                        <meshStandardMaterial  color={mainColor} side={THREE.DoubleSide} roughness={1} metalness={0.35} toneMapped={false} emissive={'#000000'} transparent={params.is_translusant || (controls.isCheckActiveTab == "doorwindow" && const_var.focusedSection !== 'center')} opacity={0.02} />
                    </mesh>

                    <mesh name="roof-in" position={[0, params.p_h, -roofLength / 2]}>
                        <extrudeGeometry args={[roofInnerModel, extrudeSettings(roofLength)]} />
                        <meshBasicMaterial map={texture_metalSprite}  side={THREE.DoubleSide} color={"#CFCFCF"} transparent={params.is_translusant || (controls.isCheckActiveTab == "doorwindow" && const_var.focusedSection !== 'center')} opacity={0.02}  />
                    </mesh>
                </>
            }
            
            {(params.p_r_s === 2 || params.p_r_s === 3) &&
                <>
                    {!params.singleSlope &&
                        <mesh name="roof-out-right" geometry={roofComplexModel.rightRoofMeshFinal} position={[0, 0.013, 0]}>
                            <meshStandardMaterial aoMap={texture} aoMapIntensity={(isPattern) ? 0 : 0.5} color={mainColor} side={THREE.DoubleSide} roughness={1} metalness={0.35} toneMapped={false} emissive={(isPattern) ? '#000000' : '#101010'} transparent={params.is_translusant || (controls.isCheckActiveTab == "doorwindow" && const_var.focusedSection !== 'center')} opacity={0.02} />
                        </mesh>
                    }
                    {(params.add_right_lean && isEqualRightHeight) && 
                        <mesh name="right-end-async-roof" geometry={roofComplexModel.rightEndRoofMeshFinal} position={[0, 0.013, 0]}>
                            <meshStandardMaterial  color={mainColor} side={THREE.DoubleSide} roughness={1} metalness={0.35} toneMapped={false} emissive={'#000000'} transparent={params.is_translusant || (controls.isCheckActiveTab == "doorwindow" && const_var.focusedSection !== 'center')} opacity={0.02} />
                        </mesh>
                    }
                    <mesh name="roof-out-left" geometry={roofComplexModel.leftRoofMeshFinal} position={[0, 0.013, 0]}>
                        <meshStandardMaterial  aoMap={texture} aoMapIntensity={(isPattern) ? 0 : 0.5} color={mainColor} side={THREE.DoubleSide} roughness={1} metalness={0.35} toneMapped={false} emissive={(isPattern) ? '#000000' : '#101010'} transparent={params.is_translusant || (controls.isCheckActiveTab == "doorwindow" && const_var.focusedSection !== 'center')} opacity={0.02} />
                    </mesh>

                    {(params.add_left_lean && isEqualLeftHeight) && 
                        <mesh name="left-end-async-roof" geometry={roofComplexModel.leftEndRoofMeshFinal} position={[0, 0.013, 0]}>
                            <meshStandardMaterial  color={mainColor} side={THREE.DoubleSide} roughness={1} metalness={0.35} toneMapped={false} emissive={'#000000'} transparent={params.is_translusant || (controls.isCheckActiveTab == "doorwindow" && const_var.focusedSection !== 'center')} opacity={0.02} />
                        </mesh>
                    }

                    <mesh name="roof-in" position={[0, params.p_h + 0.01, -roofLength / 2]}>
                        <extrudeGeometry args={[roofModel, extrudeSettings(roofLength)]} />
                        <meshBasicMaterial map={texture_metalSprite}  side={THREE.DoubleSide} color={"#CFCFCF"} transparent={params.is_translusant || (controls.isCheckActiveTab == "doorwindow" && const_var.focusedSection !== 'center')} opacity={0.02}  />
                    </mesh>

                    {/* outline for roof sides */}
                    {/* {params.singleSlope && */}
                    {params.p_r_s !== 1 &&
                        <>
                            <mesh name="roof-outline-right" position={[0, params.p_h + roofOutlineThickness - 0.01, -roofLength / 2]}>
                                <extrudeGeometry args={[roofModelOutlineRight, extrudeSettings(roofLength)]} />
                                <meshStandardMaterial side={THREE.DoubleSide} color={mainColor} roughness={1} metalness={0.35} toneMapped={false} emissive={(isPattern) ? '#000000' : '#101010'} transparent={params.is_translusant || (controls.isCheckActiveTab == "doorwindow" && const_var.focusedSection !== 'center')} opacity={0.02} />
                            </mesh>
                            <mesh name="roof-outline-left" position={[0, params.p_h + roofOutlineThickness - dynamicHeight - 0.01, -roofLength / 2]}>
                                <extrudeGeometry args={[roofModelOutlineLeft, extrudeSettings(roofLength)]} />
                                <meshStandardMaterial side={THREE.DoubleSide} color={mainColor} roughness={1} metalness={0.35} toneMapped={false} emissive={(isPattern) ? '#000000' : '#101010'} transparent={params.is_translusant || (controls.isCheckActiveTab == "doorwindow" && const_var.focusedSection !== 'center')} opacity={0.02} />
                            </mesh>
                            <mesh name="roof-outline-front" position={[0, params.p_h + roofOutlineThickness - 0.01, roofLength / 2]}>
                                <extrudeGeometry args={[roofModelOutlineFrontBack, extrudeSettings(0.001)]} />
                                <meshStandardMaterial side={THREE.DoubleSide} color={mainColor} roughness={1} metalness={0.35} toneMapped={false} emissive={(isPattern) ? '#000000' : '#101010'} transparent={params.is_translusant || (controls.isCheckActiveTab == "doorwindow" && const_var.focusedSection !== 'center')} opacity={0.02} />
                            </mesh>
                            <mesh name="roof-outline-back" position={[0, params.p_h + roofOutlineThickness - 0.01, -roofLength / 2 - 0.001]}>
                                <extrudeGeometry args={[roofModelOutlineFrontBack, extrudeSettings(0.001)]} />
                                <meshStandardMaterial side={THREE.DoubleSide} color={mainColor} roughness={1} metalness={0.35} toneMapped={false} emissive={(isPattern) ? '#000000' : '#101010'} transparent={params.is_translusant || (controls.isCheckActiveTab == "doorwindow" && const_var.focusedSection !== 'center')} opacity={0.02} />
                            </mesh>
                        </>
                    }
                    {/*End - outline for roof sides */}

                </>
            }
            {params.p_r_s === 3 && !params.singleSlope &&
                <mesh name="top-ridge-model" position={[0, params.p_h + 0.073 + ridgeThickness, -roofLength / 2]}>
                    <extrudeGeometry args={[topRidgeModel, extrudeSettings(roofLength)]} />
                    <meshStandardMaterial  color={mainColor} side={THREE.DoubleSide} roughness={1} metalness={0.35} toneMapped={false} emissive={(isPattern) ? '#000000' : '#101010'} transparent={params.is_translusant || (controls.isCheckActiveTab == "doorwindow" && const_var.focusedSection !== 'center')} opacity={0.02} />
                </mesh>
            }
        </group>
    );
};

export default RoofModel;
